import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  X,
} from "react-feather";

import { toast } from "react-toastify";

import ConfirmModal from "../../common_old/ConfirmModal";

import "../../../css/master-common.css";

import {
  clearSalesExecutiveError,
  createSalesExecutiveThunk,
  deleteSalesExecutiveThunk,
  fetchSalesExecutivesThunk,
  updateSalesExecutiveThunk,
} from "../../../store/slices/salesExecutiveSlice";

import { getPerm } from "../../../utils/perm";

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  {
    value: "sales_executive_id_desc",
    sort_by: "sales_executive_id",
    sort_order: "desc",
    label: "Latest First",
  },
  {
    value: "sales_executive_id_asc",
    sort_by: "sales_executive_id",
    sort_order: "asc",
    label: "Oldest First",
  },
  {
    value: "emp_code_asc",
    sort_by: "emp_code",
    sort_order: "asc",
    label: "Employee Code (A-Z)",
  },
  {
    value: "emp_code_desc",
    sort_by: "emp_code",
    sort_order: "desc",
    label: "Employee Code (Z-A)",
  },
  {
    value: "name_asc",
    sort_by: "name",
    sort_order: "asc",
    label: "Name (A-Z)",
  },
  {
    value: "name_desc",
    sort_by: "name",
    sort_order: "desc",
    label: "Name (Z-A)",
  },
  {
    value: "joined_on_desc",
    sort_by: "joined_on",
    sort_order: "desc",
    label: "Joined Date (Latest)",
  },
  {
    value: "joined_on_asc",
    sort_by: "joined_on",
    sort_order: "asc",
    label: "Joined Date (Oldest)",
  },
];

const STATUS_OPTIONS = [
  {
    value: "all",
    label: "All Status",
  },
  {
    value: "Active",
    label: "Active",
  },
  {
    value: "Inactive",
    label: "Inactive",
  },
];

// ============================================================
// INITIAL FORM
// ============================================================

const initialForm = {
  sales_executive_id: null,

  sales_executive_emp_code: "",

  sales_executive_mobile_no: "",

  sales_executive_name: "",

  sales_executive_joined_on: "",

  sales_executive_status: "Active",
};

// ============================================================
// COMPONENT
// ============================================================

export default function SalesExecutivePage() {
  const dispatch = useDispatch();

  // ============================================================
  // REDUX
  // ============================================================

  const {
    list,
    pagination,
    loading,
    saving,
    error,
  } = useSelector(
    (state) =>
      state.salesExecutives || {
        list: [],
        pagination: {},
        loading: false,
        saving: false,
        error: null,
      }
  );

  // ============================================================
  // PERMISSIONS
  // ============================================================

  const permsMap =
    useSelector(
      (state) =>
        state.permissions?.map
    ) || {};

  const permissions =
    getPerm(
      permsMap,
      "sales_executives"
    ) || {};

  const canCreate =
    !!permissions.can_create;

  const canUpdate =
    !!permissions.can_update;

  const canDelete =
    !!permissions.can_delete;

  // ============================================================
  // MODAL
  // ============================================================

  const [showModal, setShowModal] =
    useState(false);

  const [mode, setMode] =
    useState("create");

  const [form, setForm] =
    useState(initialForm);

  // ============================================================
  // DELETE
  // ============================================================

  const [
    showDeleteModal,
    setShowDeleteModal,
  ] = useState(false);

  const [
    deleteTarget,
    setDeleteTarget,
  ] = useState(null);

  const [
    deleting,
    setDeleting,
  ] = useState(false);

  // ============================================================
  // FILTERS
  // ============================================================

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [
    sortValue,
    setSortValue,
  ] = useState(
    "sales_executive_id_desc"
  );

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  // ============================================================
  // SORT DETAILS
  // ============================================================

  const selectedSort =
    useMemo(() => {
      return (
        SORT_OPTIONS.find(
          (item) =>
            item.value === sortValue
        ) || SORT_OPTIONS[0]
      );
    }, [sortValue]);

  const sortLabel =
    selectedSort?.label ||
    "Sort";

  const statusLabel =
    STATUS_OPTIONS.find(
      (item) =>
        item.value === statusFilter
    )?.label || "All Status";

  // ============================================================
  // LOAD DATA
  // ============================================================

  const loadData = () => {
    dispatch(
      fetchSalesExecutivesThunk({
        page: currentPage,

        page_size: PAGE_SIZE,

        search:
          searchTerm || "",

        status:
          statusFilter === "all"
            ? ""
            : statusFilter,

        sort_by:
          selectedSort.sort_by,

        sort_order:
          selectedSort.sort_order,
      })
    );
  };

  useEffect(() => {
    loadData();
  }, [
    dispatch,
    currentPage,
    searchTerm,
    statusFilter,
    selectedSort.sort_by,
    selectedSort.sort_order,
  ]);

  // ============================================================
  // PAGINATION VALUES
  // ============================================================

  const total =
    Number(
      pagination?.total || 0
    );

  const totalPages =
    Number(
      pagination?.total_pages || 0
    );

  const pageSize =
    Number(
      pagination?.page_size ||
        PAGE_SIZE
    );

  const firstRow =
    total === 0
      ? 0
      : (currentPage - 1) *
          pageSize +
        1;

  const lastRow =
    Math.min(
      currentPage * pageSize,
      total
    );

  // ============================================================
  // SEARCH
  // ============================================================

  const submitSearch = (e) => {
    e.preventDefault();

    setCurrentPage(1);

    setSearchTerm(
      String(
        searchInput || ""
      ).trim()
    );
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // ============================================================
  // CREATE
  // ============================================================

  const openCreate = () => {
    if (!canCreate) return;

    setMode("create");

    setForm({
      ...initialForm,
    });

    setShowModal(true);

    dispatch(
      clearSalesExecutiveError()
    );
  };

  // ============================================================
  // EDIT
  // ============================================================

  const openEdit = (row) => {
    if (!canUpdate) return;

    setMode("edit");

    setForm({
      sales_executive_id:
        row.sales_executive_id,

      sales_executive_emp_code:
        row.sales_executive_emp_code ||
        "",

      sales_executive_mobile_no:
        row.sales_executive_mobile_no ||
        "",

      sales_executive_name:
        row.sales_executive_name ||
        "",

      sales_executive_joined_on:
        row.sales_executive_joined_on ||
        "",

      sales_executive_status:
        row.sales_executive_status ||
        "Active",
    });

    setShowModal(true);

    dispatch(
      clearSalesExecutiveError()
    );
  };

  // ============================================================
  // CLOSE MODAL
  // ============================================================

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);

    setForm(initialForm);
  };

  // ============================================================
  // FORM CHANGE
  // ============================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // VALIDATION
  // ============================================================

  const validateForm = () => {
    const empCode =
      String(
        form.sales_executive_emp_code ||
          ""
      ).trim();

    const name =
      String(
        form.sales_executive_name ||
          ""
      ).trim();

    const mobile =
      String(
        form.sales_executive_mobile_no ||
          ""
      ).trim();

    if (!empCode) {
      toast.error(
        "Employee code is required"
      );

      return false;
    }

    if (!name) {
      toast.error(
        "Sales executive name is required"
      );

      return false;
    }

    if (
      mobile &&
      !/^[0-9]{10,15}$/.test(
        mobile
      )
    ) {
      toast.error(
        "Enter a valid mobile number"
      );

      return false;
    }

    return true;
  };

  // ============================================================
  // SAVE
  // ============================================================

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (
      mode === "create" &&
      !canCreate
    ) {
      return;
    }

    if (
      mode === "edit" &&
      !canUpdate
    ) {
      return;
    }

    const payload = {
      sales_executive_emp_code:
        String(
          form.sales_executive_emp_code
        ).trim(),

      sales_executive_mobile_no:
        String(
          form.sales_executive_mobile_no ||
            ""
        ).trim(),

      sales_executive_name:
        String(
          form.sales_executive_name
        ).trim(),

      sales_executive_joined_on:
        form.sales_executive_joined_on ||
        null,

      sales_executive_status:
        form.sales_executive_status ||
        "Active",
    };

    // ========================================================
    // CREATE
    // ========================================================

    if (mode === "create") {
      const result =
        await dispatch(
          createSalesExecutiveThunk(
            payload
          )
        );

      if (
        createSalesExecutiveThunk.fulfilled.match(
          result
        )
      ) {
        toast.success(
          "Sales executive created successfully"
        );

        setShowModal(false);

        setForm(initialForm);

        setCurrentPage(1);

        loadData();
      } else {
        toast.error(
          result?.payload ||
            result?.error?.message ||
            "Failed to create sales executive"
        );
      }

      return;
    }

    // ========================================================
    // UPDATE
    // ========================================================

    const result =
      await dispatch(
        updateSalesExecutiveThunk({
          sales_executive_id:
            form.sales_executive_id,

          ...payload,
        })
      );

    if (
      updateSalesExecutiveThunk.fulfilled.match(
        result
      )
    ) {
      toast.success(
        "Sales executive updated successfully"
      );

      setShowModal(false);

      setForm(initialForm);

      loadData();
    } else {
      toast.error(
        result?.payload ||
          result?.error?.message ||
          "Failed to update sales executive"
      );
    }
  };

  // ============================================================
  // DELETE OPEN
  // ============================================================

  const onDelete = (row) => {
    if (!canDelete) return;

    setDeleteTarget(row);

    setShowDeleteModal(true);
  };

  // ============================================================
  // DELETE CANCEL
  // ============================================================

  const cancelDelete = () => {
    if (deleting) return;

    setShowDeleteModal(false);

    setDeleteTarget(null);
  };

  // ============================================================
  // DELETE CONFIRM
  // ============================================================

  const confirmDelete =
    async () => {
      if (!deleteTarget) return;

      try {
        setDeleting(true);

        const result =
          await dispatch(
            deleteSalesExecutiveThunk({
              sales_executive_id:
                deleteTarget.sales_executive_id,
            })
          );

        if (
          deleteSalesExecutiveThunk.fulfilled.match(
            result
          )
        ) {
          toast.success(
            "Sales executive deleted successfully"
          );

          setShowDeleteModal(
            false
          );

          setDeleteTarget(null);

          // If last item on page deleted,
          // move one page back if needed.
          if (
            (list || []).length ===
              1 &&
            currentPage > 1
          ) {
            setCurrentPage(
              (prev) => prev - 1
            );
          } else {
            loadData();
          }
        } else {
          toast.error(
            result?.payload ||
              result?.error?.message ||
              "Failed to delete sales executive"
          );
        }
      } finally {
        setDeleting(false);
      }
    };

  // ============================================================
  // PAGINATION ITEMS
  // ============================================================

  const getPaginationItems =
    () => {
      if (totalPages <= 1) {
        return null;
      }

      const items = [];

      const maxVisiblePages = 5;

      const goTo = (page) => {
        const nextPage =
          Math.min(
            Math.max(1, page),
            totalPages
          );

        setCurrentPage(
          nextPage
        );
      };

      items.push(
        <Pagination.Prev
          key="prev"
          disabled={
            currentPage === 1
          }
          onClick={() =>
            goTo(
              currentPage - 1
            )
          }
        >
          <ChevronLeft
            size={16}
            className="me-1"
          />

          Prev
        </Pagination.Prev>
      );

      let startPage =
        Math.max(
          1,
          currentPage -
            Math.floor(
              maxVisiblePages / 2
            )
        );

      let endPage =
        Math.min(
          totalPages,
          startPage +
            maxVisiblePages -
            1
        );

      if (
        endPage -
          startPage +
          1 <
        maxVisiblePages
      ) {
        startPage =
          Math.max(
            1,
            endPage -
              maxVisiblePages +
              1
          );
      }

      if (startPage > 1) {
        items.push(
          <Pagination.Item
            key={1}
            onClick={() =>
              goTo(1)
            }
          >
            1
          </Pagination.Item>
        );

        if (startPage > 2) {
          items.push(
            <Pagination.Ellipsis
              key="start-ellipsis"
            />
          );
        }
      }

      for (
        let page = startPage;
        page <= endPage;
        page++
      ) {
        items.push(
          <Pagination.Item
            key={page}
            active={
              page === currentPage
            }
            onClick={() =>
              goTo(page)
            }
          >
            {page}
          </Pagination.Item>
        );
      }

      if (
        endPage < totalPages
      ) {
        if (
          endPage <
          totalPages - 1
        ) {
          items.push(
            <Pagination.Ellipsis
              key="end-ellipsis"
            />
          );
        }

        items.push(
          <Pagination.Item
            key={totalPages}
            onClick={() =>
              goTo(totalPages)
            }
          >
            {totalPages}
          </Pagination.Item>
        );
      }

      items.push(
        <Pagination.Next
          key="next"
          disabled={
            currentPage ===
            totalPages
          }
          onClick={() =>
            goTo(
              currentPage + 1
            )
          }
        >
          Next

          <ChevronRight
            size={16}
            className="ms-1"
          />
        </Pagination.Next>
      );

      return items;
    };

  // ============================================================
  // JSX
  // ============================================================

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">
            Sales Executives
          </h4>
        </div>

        <Button
          onClick={openCreate}
          disabled={!canCreate}
          className="mm-primary-btn d-flex align-items-center gap-2"
          title={
            !canCreate
              ? "No create permission"
              : "Add sales executive"
          }
        >
          <Plus size={18} />

          Add Sales Executive
        </Button>
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
          dismissible
          onClose={() =>
            dispatch(
              clearSalesExecutiveError()
            )
          }
        >
          <div className="fw-bold mb-1">
            Error
          </div>

          <div>
            {String(error)}
          </div>
        </Alert>
      ) : null}

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">

            {/* Search */}

            <Col lg={6} md={6}>
              <Form.Label className="fw-bold mb-1">
                Search
              </Form.Label>

              <Form
                onSubmit={
                  submitSearch
                }
              >
                <InputGroup>
                  <InputGroup.Text className="mm-inputgroup-text">
                    <Search
                      size={18}
                      className="text-muted"
                    />
                  </InputGroup.Text>

                  <FormControl
                    className="mm-input mm-inputgroup-control"
                    placeholder="Search by Employee Code / Name / Mobile..."
                    value={
                      searchInput
                    }
                    onChange={(e) =>
                      setSearchInput(
                        e.target.value
                      )
                    }
                  />

                  {searchInput ? (
                    <Button
                      type="button"
                      variant="light"
                      className="mm-clear-btn"
                      onClick={
                        clearSearch
                      }
                    >
                      <X size={16} />
                    </Button>
                  ) : null}

                  <Button
                    type="submit"
                    className="mm-primary-btn"
                  >
                    Search
                  </Button>
                </InputGroup>
              </Form>
            </Col>

            {/* Sort */}

            <Col lg={3} md={3}>
              <Form.Label className="fw-bold mb-1">
                Sort
              </Form.Label>

              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />

                    {sortLabel}
                  </span>

                  <ChevronDown
                    size={16}
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu w-100">
                  {SORT_OPTIONS.map(
                    (option) => (
                      <Dropdown.Item
                        key={
                          option.value
                        }
                        active={
                          sortValue ===
                          option.value
                        }
                        onClick={() => {
                          setSortValue(
                            option.value
                          );

                          setCurrentPage(
                            1
                          );
                        }}
                      >
                        {
                          option.label
                        }
                      </Dropdown.Item>
                    )
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            {/* Status */}

            <Col lg={3} md={3}>
              <Form.Label className="fw-bold mb-1">
                Status
              </Form.Label>

              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />

                    {statusLabel}
                  </span>

                  <ChevronDown
                    size={16}
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu w-100">
                  {STATUS_OPTIONS.map(
                    (option) => (
                      <Dropdown.Item
                        key={
                          option.value
                        }
                        active={
                          statusFilter ===
                          option.value
                        }
                        onClick={() => {
                          setStatusFilter(
                            option.value
                          );

                          setCurrentPage(
                            1
                          );
                        }}
                      >
                        {
                          option.label
                        }
                      </Dropdown.Item>
                    )
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            {/* Showing */}

            <Col
              md={12}
              className="text-end"
            >
              <div className="text-muted small">
                Showing{" "}

                <span className="fw-bold">
                  {firstRow}-
                  {lastRow}
                </span>{" "}

                of{" "}

                <span className="fw-bold">
                  {total}
                </span>{" "}

                sales executives
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />

              <div className="mt-2 text-muted">
                Loading sales executives...
              </div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table
                  hover
                  className="mm-table mb-0"
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: 90,
                        }}
                      >
                        Sl. No.
                      </th>

                      <th
                        style={{
                          minWidth: 150,
                        }}
                      >
                        Employee Code
                      </th>

                      <th
                        style={{
                          minWidth: 220,
                        }}
                      >
                        Name
                      </th>

                      <th
                        style={{
                          minWidth: 160,
                        }}
                      >
                        Mobile No.
                      </th>

                      <th
                        style={{
                          minWidth: 150,
                        }}
                      >
                        Joined On
                      </th>

                      <th
                        style={{
                          minWidth: 120,
                        }}
                      >
                        Status
                      </th>

                      <th
                        className="mm-actions-th"
                        style={{
                          width: 160,
                          textAlign:
                            "right",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {!list?.length ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="p-5 text-center"
                        >
                          <div className="mm-empty-icon mx-auto mb-2">
                            <Search
                              size={22}
                            />
                          </div>

                          <div className="fw-bold mb-1">
                            {searchTerm
                              ? "No results found"
                              : "No sales executives found"}
                          </div>

                          <div className="text-muted">
                            {searchTerm
                              ? "No sales executives match your search."
                              : "Get started by adding your first sales executive."}
                          </div>

                          {searchTerm ? (
                            <Button
                              variant="outline-primary"
                              className="mt-3"
                              onClick={
                                clearSearch
                              }
                            >
                              Clear Search
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ) : (
                      list.map(
                        (
                          row,
                          index
                        ) => (
                          <tr
                            key={
                              row.sales_executive_id
                            }
                          >
                            <td className="fw-semibold">
                              {(currentPage -
                                1) *
                                pageSize +
                                index +
                                1}
                            </td>

                            <td className="fw-semibold">
                              {row.sales_executive_emp_code ||
                                "-"}
                            </td>

                            <td>
                              {row.sales_executive_name ||
                                "-"}
                            </td>

                            <td>
                              {row.sales_executive_mobile_no ||
                                "-"}
                            </td>

                            <td>
                              {row.sales_executive_joined_on ||
                                "-"}
                            </td>

                            <td>
                              <span className="mm-pill">
                                {row.sales_executive_status ||
                                  "-"}
                              </span>
                            </td>

                            <td
                              className="mm-actions-td"
                              style={{
                                textAlign:
                                  "right",
                              }}
                            >
                              <div className="d-inline-flex gap-2">
                                <Button
                                  variant="light"
                                  className="mm-icon-btn primary"
                                  disabled={
                                    !canUpdate ||
                                    saving
                                  }
                                  title={
                                    !canUpdate
                                      ? "No update permission"
                                      : "Edit"
                                  }
                                  onClick={() =>
                                    openEdit(
                                      row
                                    )
                                  }
                                >
                                  <Edit2
                                    size={
                                      16
                                    }
                                  />
                                </Button>

                                <Button
                                  variant="light"
                                  className="mm-icon-btn danger"
                                  disabled={
                                    !canDelete ||
                                    saving
                                  }
                                  title={
                                    !canDelete
                                      ? "No delete permission"
                                      : "Delete"
                                  }
                                  onClick={() =>
                                    onDelete(
                                      row
                                    )
                                  }
                                >
                                  <Trash2
                                    size={
                                      16
                                    }
                                  />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3 border-top mm-footer">
                  <Pagination className="mb-0 master-pagination">
                    {getPaginationItems()}
                  </Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      {/* =====================================================
          CREATE / EDIT MODAL
      ===================================================== */}

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        size="lg"
        className="mm-modal"
      >
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {mode === "create"
                ? "Add Sales Executive"
                : "Edit Sales Executive"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">

              {/* Employee Code */}

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Employee Code{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Control
                    className="mm-input"
                    name="sales_executive_emp_code"
                    value={
                      form.sales_executive_emp_code
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter employee code"
                    autoFocus
                  />
                </Form.Group>
              </Col>

              {/* Name */}

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Name{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Control
                    className="mm-input"
                    name="sales_executive_name"
                    value={
                      form.sales_executive_name
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter sales executive name"
                  />
                </Form.Group>
              </Col>

              {/* Mobile */}

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Mobile No.
                  </Form.Label>

                  <Form.Control
                    className="mm-input"
                    name="sales_executive_mobile_no"
                    value={
                      form.sales_executive_mobile_no
                    }
                    onChange={(e) => {
                      const value =
                        e.target.value.replace(
                          /\D/g,
                          ""
                        );

                      setForm(
                        (prev) => ({
                          ...prev,
                          sales_executive_mobile_no:
                            value.slice(
                              0,
                              15
                            ),
                        })
                      );
                    }}
                    placeholder="Enter mobile number"
                    inputMode="numeric"
                  />
                </Form.Group>
              </Col>

              {/* Joined Date */}

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Joined On
                  </Form.Label>

                  <Form.Control
                    type="date"
                    className="mm-input"
                    name="sales_executive_joined_on"
                    value={
                      form.sales_executive_joined_on
                    }
                    onChange={
                      handleChange
                    }
                  />
                </Form.Group>
              </Col>

              {/* Status */}

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Status
                  </Form.Label>

                  <Form.Select
                    className="mm-select"
                    name="sales_executive_status"
                    value={
                      form.sales_executive_status
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <option value="Active">
                      Active
                    </option>

                    <option value="Inactive">
                      Inactive
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Permission Warning */}

              {(mode === "create" &&
                !canCreate) ||
              (mode === "edit" &&
                !canUpdate) ? (
                <Col md={12}>
                  <Alert variant="warning">
                    You don't have
                    permission to{" "}
                    {mode === "create"
                      ? "create"
                      : "update"}{" "}
                    sales executives.
                  </Alert>
                </Col>
              ) : null}
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={saving}
              className="mm-cancel-btn"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              className="mm-primary-btn"
              disabled={
                saving ||
                !String(
                  form.sales_executive_emp_code ||
                    ""
                ).trim() ||
                !String(
                  form.sales_executive_name ||
                    ""
                ).trim() ||
                (mode ===
                  "create" &&
                  !canCreate) ||
                (mode ===
                  "edit" &&
                  !canUpdate)
              }
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    animation="border"
                    className="me-2"
                  />

                  Saving...
                </>
              ) : mode ===
                "create" ? (
                "Save Sales Executive"
              ) : (
                "Update Sales Executive"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* =====================================================
          DELETE CONFIRM
      ===================================================== */}

      <ConfirmModal
        show={
          showDeleteModal
        }
        title="Delete Sales Executive"
        message={`Delete sales executive "${
          deleteTarget
            ?.sales_executive_name ||
          ""
        }"?`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={
          cancelDelete
        }
        onConfirm={
          confirmDelete
        }
      />
    </div>
  );
}